import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { Post } from '@sodamnfoolish/sjc-backend-types/src/api/feed/dto/post';
import { toast } from 'react-toastify';
import { TelegramLogin } from '../../Telegram/TelegramLogin';
import { useTelegram } from '../../Telegram/UseTelegram';
import { RegisterChannel } from '../../Telegram/RegisterChannel';
import { MediaSource } from '@sodamnfoolish/sjc-backend-types/src/api/feed/media-source';
import { useBackend } from '../../../Hooks/UseBackend';
import { ExternalMedia, InternalMedia } from '@sodamnfoolish/sjc-backend-types/src/api/feed/dto';
import pumpFunApi from '../../../utils/pumpfun/pump-fun-api';
import { useUrlParams } from '../../../Hooks/useUrlParams';
import { Connection, Keypair, LAMPORTS_PER_SOL, PublicKey } from '@solana/web3.js';
import { IMAGE, URL } from '../../../utils/source-constants';
import { base64ToFile } from '../../../utils/base64-to-file';
import { urlToFile } from '../../../utils/url-to-file';
import bs58 from 'bs58';
import { providers, Wallet } from 'ethers';
import { rpcUrls } from '../../../utils/rpc-urls';
import { useERC20Token } from '../../../Hooks/UseERC20Token';
import { Network, solididtyReleases } from '../../../utils/types';
import { Api } from '@sodamnfoolish/sjc-backend-types';
import { getRSAEncryptedString } from '../../../utils/RSA';
import { zeroslotDeployTip } from '../../../utils/constants';
import { useClipboard } from '../../../Hooks/useClipboars';
import { RefWrapper } from '../../RefWrapper';
import { TweetActions } from './TweetActions';
import { Card } from './Card';
import { cropCenter, ImageEditor } from './ImageEditor';
import { AIImageGenerator } from './AIImageGenerator';

import '../../Styles/TokenCreate.css';
import { capitalizeFirstLetter } from '../../../utils/string';
import { Instant } from './Instant';

const preventSymbols = (event: { key: string; preventDefault: () => void }) => {
    const validCharacters = /^[a-zA-Z0-9_]+$/;

    if (!validCharacters.test(event.key)) {
        event.preventDefault();
    }
};

const removeEmojis = (text: string) => {
    return text.replace(
        /([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF\uDC00-\uDFFF\u0023-\u0039]\uFE0F?|[\u200D\u20E3\u3297\u3299]\uFE0F?|[\u00A9\u00AE\u303D\u3030\u2B50\u2B55\u231A\u231B\u25AA\u25AB\u25B6\u25C0\u25FB\u25FC\u25FD\u25FE\u00A9\u00AE\u303D\u3030\u2B50\u2B55\u231A\u231B\u25AA\u25AB\u25B6\u25C0\u25FB\u25FC\u25FD\u25FE])+|\uD83D[\uDC00-\uDFFF]/g,
        '',
    );
};

const getFirstWord = (text: string, caps: boolean = false): string => {
    if (!text) return '';

    const removeUnwantedSegments = (input: string): string => {
        let result = input.replace(/::\[Reply\]/gi, '');
        result = result.replace(/@\w+/g, '');
        result = result.replace(/\d{2}:\d{2}:\d{2}/g, '');
        result = result.replace(/\[.*?\]/g, '');
        result = result.replace(/[^\w\s]|_/g, '');
        return result.trim();
    };

    const cleanText = removeEmojis(removeUnwantedSegments(text));

    let words = cleanText.split(/\s+/).filter((word) => word.length > 0);

    words = words.filter((word) => word.length > 1);

    let firstWord = '';

    if (cleanText.toLowerCase().startsWith('just in')) {
        const wordsAfterKeyword = words.slice(2);
        firstWord = wordsAfterKeyword[0] || '';
    } else if (cleanText.toLowerCase().startsWith('breaking')) {
        const wordsAfterKeyword = words.slice(1);
        firstWord = wordsAfterKeyword[0] || '';
    } else if (cleanText.toLowerCase().startsWith('rt')) {
        const wordsAfterKeyword = words.slice(1);
        firstWord = wordsAfterKeyword[0] || '';
    } else {
        firstWord = words[0] || '';
    }

    return caps ? firstWord.toUpperCase() : firstWord;
};

const removeDoubleStar = (text: string) => {
    return text.replaceAll('*', '');
};

const blobToFile = (blob: Blob, filename: string): File => {
    return new File([blob], filename, { type: blob.type });
};
const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject('Error converting blob to Base64');
        reader.readAsDataURL(blob);
    });
};

const extractTextFromImage = async (imageFile: File | Blob) => {
    const formData = new FormData();
    formData.append('file', imageFile);
    formData.append('apikey', process.env.REACT_APP_OCR_API_KEY || '');

    try {
        const response = await fetch('https://api.ocr.space/parse/image', {
            method: 'POST',
            body: formData,
        });
        const result = await response.json();

        if (result.IsErroredOnProcessing) {
            console.error('Error in OCR processing:', result.ErrorMessage);
            return '';
        }

        return result.ParsedResults[0]?.ParsedText || '';
    } catch (error) {
        console.error('Error while calling OCR API:', error);
        return '';
    }
};

const formatWalletAddress = (address: string | undefined) => {
    if (!address) return '';
    if (address.length <= 10) return address;
    const start = address.slice(0, 5);
    const end = address.slice(-5);
    return `${start}...${end}`;
};

function getSourceDetails(platform?: string, username?: string) {
    const getDetails = <T extends Record<string, any>>(base: Record<string, T>, platform: string) => {
        return base[platform] && (base[platform][username as keyof T] as string);
    };

    const image = getDetails(IMAGE, platform || '');
    const url = getDetails(URL, platform || '');

    return { image, url };
}

export function TokenCreate({
    solInitialBuyArrow,
    solInitialBuyCopy,

    ethLiquidityArrow,
    ethLiquidityCopy,

    solSnipeArrow,
    solSnipeCopy,

    ethSnipeArrow,
    ethSnipeCopy,

    autoSellDelayArrow,
    autoSellProfitArrow,
    autoSellDelayCreation,
    autoSellProfitCreation,
    ethPrivateKey,
    solPrivateKey,
    solSniperPrivateKey,
    ethSniperPrivateKey,
    solidityReleases,
    availableNetworks,
    zeroSlotBuyTip,
    buyPrio,
    solConnection,
}: {
    solInitialBuyArrow: number;
    solInitialBuyCopy: number;

    ethLiquidityArrow: number;
    ethLiquidityCopy: number;

    solSnipeArrow: number;
    solSnipeCopy: number;

    ethSnipeArrow: number;
    ethSnipeCopy: number;

    autoSellDelayArrow: number;
    autoSellProfitArrow: number;
    autoSellDelayCreation: number;
    autoSellProfitCreation: number;
    ethPrivateKey: string;
    solPrivateKey: string;
    solSniperPrivateKey: string;
    ethSniperPrivateKey: string;
    solidityReleases: solididtyReleases;
    availableNetworks: Network[];
    zeroSlotBuyTip: number;
    buyPrio: number;
    solConnection: Connection;
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const {
        post,
        background,
        platform,
        token,
    }: {
        post?: Post;
        background?: string;
        platform?: string;
        token?: {
            name?: string;
            symbol?: string;
            description?: string;
            image?: string;
            telegram?: string;
            twitter?: string;
            website?: string;
        };
    } = location.state || {};

    const { image: profilePic, url: postUrl } = getSourceDetails(platform, post?.username);

    const { getMedia, getChatGptMetadataFromPostId, deployWithSnipe } = useBackend();
    const { deployETHToken } = useERC20Token();

    const { authorized } = useTelegram();

    const [network, setNetwork] = useState<Network>(availableNetworks[0]);
    const [ethWalletBalance, setEthWalletBalance] = useState<number>(0);
    const [ethAutoOpen, setEthAutoOpen] = useState<boolean>(true);
    const [walletBalance, setWalletBalance] = useState<number>(0);

    const base64ProfilePic = profilePic ? `data:image/jpeg;base64,${profilePic}` : null;
    const [imageSrc, setImageSrc] = useState<string | null>(base64ProfilePic);
    const [imageFile, setImageFile] = useState<File | null>(base64ProfilePic ? base64ToFile(base64ProfilePic, 'sourceImage.jpg') : null);

    const [name, setName] = useState<string>(() => {
        let name = '';
        if (post && post.text) {
            name = capitalizeFirstLetter(getFirstWord(post.text, false));
        }
        if (token && token.name) {
            name = token.name;
        }

        return name;
    });
    const [symbol, setSymbol] = useState<string>(() => {
        let symbol = '';
        if (post && post.text) {
            symbol = getFirstWord(post.text, true);
        }
        if (token && token.symbol) {
            symbol = token.symbol;
        }
        return symbol;
    });
    const [description, setDescription] = useState<string>(() => {
        let description = '';
        if (post && post.text) {
            description = removeDoubleStar(post.text);
        }
        if (token && token.description) {
            description = token.description;
        }
        return description;
    });
    const [telegram, setTelegram] = useState<string>(() => token?.telegram ?? '');
    const [twitter, setTwitter] = useState<string>(() => token?.twitter ?? '');
    const [website, setWebsite] = useState<string>(() => token?.website ?? '');

    const [symbolAutocapChecked, setSymbolAutocapChecked] = useState(true);

    const { sessionId } = useUrlParams();

    const [ethLiquidity, setEthLiquidity] = useState<number>(post ? ethLiquidityArrow : ethLiquidityCopy);
    const [ethSnipeValue, setEthSnipeValue] = useState<number>(post ? ethSnipeArrow : ethSnipeCopy);

    const [snipeEthChecked, setSnipeEthChecked] = useState(false);

    const getImageFromUrl = useCallback(async (url: string) => {
        setImageSrc(url);
        const file = await urlToFile(url, 'metadataImage.jpg', 'image/jpeg');
        setImageFile(file);
        setIsOpen(false);
    }, []);

    let walletAddress = '';
    try {
        walletAddress = solPrivateKey ? Keypair.fromSecretKey(bs58.decode(solPrivateKey)).publicKey.toBase58() : '';
    } catch (error) {
        console.log(error);
    }

    let ethWalletAddress: string | undefined = undefined;
    try {
        const wallet = new Wallet(ethPrivateKey, new providers.JsonRpcProvider(rpcUrls[network]));
        ethWalletAddress = wallet.address;
    } catch (error) {
        console.log(error);
    }

    useEffect(() => {
        let subId: number;
        if (solPrivateKey) {
            const keypair = Keypair.fromSecretKey(bs58.decode(solPrivateKey));
            solConnection.getBalance(keypair.publicKey).then((balance) => {
                setWalletBalance(balance / LAMPORTS_PER_SOL);
            });
            console.log('Subscribing to account changes for wallet:', keypair.publicKey);
            subId = solConnection.onAccountChange(new PublicKey(keypair.publicKey), (account) => {
                setWalletBalance(account.lamports / LAMPORTS_PER_SOL);
            });
        }

        return () => {
            if (subId) solConnection.removeAccountChangeListener(subId);
        };
    }, [solPrivateKey, solConnection]);

    useEffect(() => {
        function updateBalance() {
            if (!ethWalletAddress) {
                return;
            }
            const provider = new providers.JsonRpcProvider(rpcUrls[network]);
            provider.getBalance(ethWalletAddress).then((balance) => {
                setEthWalletBalance(Number(balance));
            });
        }

        const intervalId = setInterval(updateBalance, 1000);
        return () => clearInterval(intervalId);
    }, [ethWalletAddress, network]);

    const imageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            blobToBase64(file)
                .then((base64Image) => {
                    setImageSrc(base64Image);
                    setImageFile(file);
                })
                .catch((error) => {
                    console.error('Error converting file to Base64:', error);
                });
        }
    };

    const tokenImage = token?.image;
    useEffect(() => {
        if (tokenImage) {
            getImageFromUrl(tokenImage);
        }
    }, [getImageFromUrl, tokenImage]);

    useEffect(() => {
        const fetchFirstImage = async () => {
            if (post && post.media && post.media.length > 0) {
                const mediaItem = post.media[0];
                try {
                    if (mediaItem.source === MediaSource.Internal) {
                        const blob = await getMedia((mediaItem as InternalMedia).id);
                        const base64Image = await blobToBase64(blob);
                        setImageSrc(base64Image);
                        setImageFile(blobToFile(blob, 'postImage.jpg'));
                    } else if (mediaItem.source === MediaSource.External) {
                        const response = await fetch((mediaItem as ExternalMedia).url);
                        const blob = await response.blob();
                        const base64Image = await blobToBase64(blob);
                        setImageSrc(base64Image);
                        setImageFile(blobToFile(blob, 'postImage.jpg'));
                    }
                } catch (error) {
                    console.error('Error fetching media:', error);
                }
            }
        };

        fetchFirstImage();
    }, [getMedia, profilePic, post]);

    const getAIMetadata = async (post: Post) => {
        const metadata = await getChatGptMetadataFromPostId(post.id);
        setName(metadata.name);
        setSymbol(metadata.symbol);
        setDescription(post.url);
    };

    const deployTokenFromMetadata = async (
        name: string,
        symbol: string,
        imageFile: File | null,
        solPrivateKey: string,
        dex: 'pump' | 'bonk',
        mintSecretKeyBase58: string | undefined,
        buyAmount: number,
        solSniperPrivateKey?: string,
        snipeSolValue?: number,
    ) => {
        if (!imageFile) return toast.error('no image file');
        if (!solPrivateKey) return toast.error('no wallet address');
        toast.info('Uploading metadata...');
        const deployer = Keypair.fromSecretKey(bs58.decode(solPrivateKey));
        const uploadMetadataResponse = await pumpFunApi.uploadMetadata({
            file: imageFile,
            name: name,
            symbol: symbol,
            description: description,
            twitter: twitter,
            telegram: telegram,
            website: website,
            showName: 'true',
        });

        const requestData: Api.Wallet.DTO.DeployWithSniperRequest = {
            name: name,
            symbol: symbol,
            metadataUri: uploadMetadataResponse.metadataUri,
            deploy: {
                encodedPrivateKey: getRSAEncryptedString(solPrivateKey),
                priorityFee: buyPrio,
                zeroSlotTip: zeroslotDeployTip * LAMPORTS_PER_SOL,
                buyAmount: buyAmount,
            },
            snipers:
                solSniperPrivateKey && snipeSolValue
                    ? [
                          {
                              encodedPrivateKey: getRSAEncryptedString(solSniperPrivateKey),
                              priorityFee: buyPrio,
                              zeroSlotTip: zeroSlotBuyTip * LAMPORTS_PER_SOL,
                              amount: snipeSolValue,
                          },
                      ]
                    : [],
            mintSecretKeyBase58: mintSecretKeyBase58,
            commitment: 'processed',
            platform: dex,
        };
        console.log('Deploy request data:', requestData);

        const result = await deployWithSnipe(requestData);

        if (result) {
            const { mint, deploySignature, snipeSignatures } = result;
            toast.success(`Deploy transaction sent: ${deploySignature}.`);
            console.log('Deploy transaction sent:', deploySignature);
            snipeSignatures.forEach((signature) => {
                toast.success(`Snipe transaction sent: ${signature}.`);
            });
            console.log('Snipe transactions sent:', snipeSignatures);
            navigate(`/Result?walletAddress=${deployer.publicKey.toBase58()}`, {
                state: {
                    post,
                    background,
                    platform,
                    mint: mint,
                    name: name,
                    symbol: symbol,
                    initBuyDeployer: buyAmount / LAMPORTS_PER_SOL,
                    initBuySniper: snipeSolValue ? snipeSolValue / LAMPORTS_PER_SOL : 0,
                    autoSellDelaySeconds: post ? autoSellDelayArrow : autoSellDelayCreation,
                    takeProfitPercent: post ? autoSellProfitArrow : autoSellProfitCreation,
                    imageFile,
                    dex: dex,
                },
            });
        }
    };

    const handleExtractText = async () => {
        if (!imageFile) {
            toast.error('Upload image first');
            return;
        }

        const extractedText = await extractTextFromImage(imageFile);
        if (extractedText) {
            setDescription((prevDescription) => `${extractedText}\n${prevDescription}`);
            toast.success('Text extract successfully!');
        } else {
            toast.error('Image dont have text');
        }
    };

    const [isOpen, setIsOpen] = useState(false);

    const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            setIsOpen(false);
        }
    };

    const { copyToClipboard } = useClipboard();

    return (
        <>
            <section className='card-settings-container'>
                <Card
                    image={profilePic}
                    url={postUrl}
                    post={post}
                    background={background}
                    platform={platform}
                    onImageChange={(dataUrl, file) => {
                        setImageSrc(dataUrl);
                        setImageFile(file);
                    }}
                    onMetadataChange={(metadata) => {
                        setName(metadata.name ?? '');
                        setSymbol(metadata.symbol ?? '');
                        setDescription(metadata.description ?? '');
                        setTelegram(metadata.telegram ?? '');
                        setTwitter(metadata.twitter ?? '');
                        setWebsite(metadata.website ?? '');

                        getImageFromUrl(metadata.image ?? '');
                    }}
                />
                <section className='create-token-container'>
                    <div className='sett-naming-container'>
                        <div className='naming'>
                            <h3>Name</h3>
                            <div className='unstable_input'>
                                <input
                                    value={name}
                                    onChange={(event) => {
                                        setName(event.target.value);
                                    }}
                                    type='text'
                                />
                                <span>the creation may fail due to excessive characters</span>
                            </div>
                        </div>
                        <div className='symbols'>
                            <h3>Symbol</h3>
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <div className='unstable_input'>
                                    <input
                                        type='text'
                                        className='symbol-input'
                                        value={symbol}
                                        onChange={(event) => {
                                            let text = event.target.value;
                                            text = symbolAutocapChecked ? text.toUpperCase() : text;

                                            function trimTextToFitLimits(text: string) {
                                                const specialCharRegex = /[^a-zA-Z0-9]/g;
                                                const regularCharRegex = /[a-zA-Z0-9]/g;

                                                const specialChars = text.match(specialCharRegex) || [];
                                                const regularChars = text.match(regularCharRegex) || [];

                                                let result = '';

                                                if (
                                                    specialChars.length > 2 ||
                                                    (specialChars.length === 1 && regularChars.length > 6) ||
                                                    regularChars.length > 10
                                                ) {
                                                    if (specialChars.length > 2) {
                                                        result += specialChars.slice(0, 2).join('');
                                                    } else if (specialChars.length === 1 && regularChars.length > 6) {
                                                        result += specialChars.join('') + regularChars.slice(0, 6).join('');
                                                    } else if (regularChars.length > 10) {
                                                        result += regularChars.slice(0, 10).join('');
                                                    }
                                                } else {
                                                    result = text;
                                                }

                                                return result;
                                            }

                                            text = trimTextToFitLimits(text);
                                            setSymbol(text);
                                        }}
                                    />
                                    <span>the creation may fail due to excessive characters</span>
                                </div>
                                <input
                                    type='checkbox'
                                    className='symbol-checkbox'
                                    checked={symbolAutocapChecked}
                                    onChange={() => {
                                        if (!symbolAutocapChecked) {
                                            setSymbol(symbol.toUpperCase());
                                        }
                                        setSymbolAutocapChecked(!symbolAutocapChecked);
                                    }}
                                />
                                <h3>AutoCap</h3>
                            </div>
                        </div>
                        <div className='description-case'>
                            <h3>Description</h3>
                            <textarea
                                value={description}
                                onChange={(event) => {
                                    setDescription(event.target.value);
                                }}
                            />
                        </div>

                        <div className='description-case'>
                            <h3>AI Image</h3>
                            <AIImageGenerator
                                onImageChange={(dataUrl, file) => {
                                    setImageSrc(dataUrl);
                                    setImageFile(file);
                                }}
                            />
                        </div>

                        <div className='generate-extract-btn-case'>
                            {post ? (
                                <div className='generate-ai-btn'>
                                    <button
                                        className='ai-btn'
                                        onClick={() => {
                                            getAIMetadata(post);
                                        }}
                                    >
                                        Generate with AI
                                    </button>
                                </div>
                            ) : null}
                            {post ? (
                                <div className='extract-text-btn'>
                                    <button className='ai-btn' onClick={handleExtractText}>
                                        Text extract image
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <ImageEditor
                        imageSrc={imageSrc}
                        onImageChange={(dataUrl, file) => {
                            setImageSrc(dataUrl);
                            setImageFile(file);
                        }}
                    >
                        {(cropImage, openImageEditor) => (
                            <>
                                <div className='sett-container'>
                                    <div className='img-case'>
                                        <div className='img-btn'>
                                            <div className='img'>{imageSrc ? <img src={imageSrc} alt='Uploaded' /> : 'image from post'}</div>
                                            <div className='btn-case'>
                                                <div className='image-manipulation-btns'>
                                                    <button
                                                        data-tooltip='Remove Image'
                                                        className='delete-btn'
                                                        onClick={() => {
                                                            setImageSrc(null);
                                                            setImageFile(null);
                                                        }}
                                                    ></button>
                                                    <button className='image-editor-btn' data-tooltip='Open Image Editor' onClick={openImageEditor}></button>
                                                </div>
                                                <div className='image-manipulation-btns'>
                                                    <label className='upload-icon' data-tooltip='Upload Image'>
                                                        <input type='file' accept='image/*' onChange={imageFileChange} />
                                                    </label>
                                                    <button
                                                        className='import-menu-btn'
                                                        data-tooltip='Import Image From URL'
                                                        onClick={() => {
                                                            setIsOpen(true);
                                                        }}
                                                    ></button>
                                                    {isOpen && (
                                                        <div className='popup-container' onClick={handleOutsideClick}>
                                                            <div className='popup'>
                                                                <div className='popup-header'>
                                                                    <h3>Import Image From Web</h3>
                                                                    <button
                                                                        onClick={() => {
                                                                            setIsOpen(false);
                                                                        }}
                                                                        className='close-btn'
                                                                    >
                                                                        &times;
                                                                    </button>
                                                                </div>
                                                                <div className='import-inp-btn'>
                                                                    <RefWrapper<HTMLInputElement>>
                                                                        {(ref) => (
                                                                            <>
                                                                                <input
                                                                                    ref={ref}
                                                                                    type='text'
                                                                                    placeholder='Enter image URL'
                                                                                    className='popup-input'
                                                                                />
                                                                                <button
                                                                                    className='import-btn'
                                                                                    onClick={() => {
                                                                                        if (ref.current?.value) {
                                                                                            getImageFromUrl(ref.current.value);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    Import
                                                                                </button>
                                                                            </>
                                                                        )}
                                                                    </RefWrapper>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                {post && (platform === 'twitter' || post.username === 'elonalert') && (
                                                    <TweetActions
                                                        url={post.url}
                                                        onImageChange={(dataUrl, file) => {
                                                            setImageSrc(dataUrl);
                                                            setImageFile(file);
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='picture-buttons-case'>
                                    <button
                                        className='pic-btn'
                                        onClick={() => {
                                            cropImage(({ height, width }) => {
                                                let diff = height - width;

                                                if (diff > 0) {
                                                    return { left: 0, top: 0, width, height: height - diff };
                                                } else {
                                                    return cropCenter({ height, width });
                                                }
                                            });
                                        }}
                                    >
                                        Crop Upper
                                    </button>
                                    <button
                                        className='pic-btn'
                                        onClick={() => {
                                            cropImage(cropCenter);
                                        }}
                                    >
                                        Crop Center
                                    </button>
                                    <button
                                        className='pic-btn'
                                        onClick={() => {
                                            if (profilePic) {
                                                setImageSrc(`data:image/jpeg;base64,${profilePic}`);
                                                const file = base64ToFile(`data:image/jpeg;base64,${profilePic}`, 'profilePic.jpg');
                                                setImageFile(file);
                                            } else {
                                                console.error('No image available in source details');
                                            }
                                        }}
                                    >
                                        Profile Pic
                                    </button>
                                </div>
                            </>
                        )}
                    </ImageEditor>

                    <div className='links-container'>
                        <div className='link-case'>
                            <h3>Telegram</h3>
                            <input
                                type='text'
                                className='links'
                                value={telegram}
                                onChange={(e) => {
                                    setTelegram(e.target.value);
                                }}
                            />
                        </div>
                        <div className='link-case'>
                            <h3>Twitter</h3>
                            <input
                                type='text'
                                className='links'
                                value={twitter}
                                onChange={(e) => {
                                    setTwitter(e.target.value);
                                }}
                            />
                        </div>
                        <div className='link-case'>
                            <h3>Website</h3>
                            <input
                                type='text'
                                className='links'
                                value={website}
                                onChange={(e) => {
                                    setWebsite(e.target.value);
                                }}
                            />
                        </div>
                    </div>
                </section>
                <section className='tg-channel'>
                    <div className='send-instant-case'>
                        <Instant
                            walletAddress={walletAddress}
                            walletBalance={walletBalance}
                            defaultSnipeSolValue={post ? solSnipeArrow : solSnipeCopy}
                            defaultSolInitialBuyValue={post ? solInitialBuyArrow : solInitialBuyCopy}
                            onInstantClick={(data) => {
                                deployTokenFromMetadata(
                                    name,
                                    symbol,
                                    imageFile,
                                    solPrivateKey,
                                    data.instantType,
                                    data.mintSecretKeyBase58,
                                    data.initBuySolValue * LAMPORTS_PER_SOL,
                                    data.snipeChecked ? solSniperPrivateKey : undefined,
                                    data.snipeChecked ? data.snipeSolValue * LAMPORTS_PER_SOL : undefined,
                                );
                            }}
                        />
                        <section className='eth-deploy-block'>
                            <div className='eth-deploy-info'>
                                <label>
                                    <select value={network} onChange={(e) => setNetwork(e.target.value as Network)}>
                                        {availableNetworks.map((network) => (
                                            <option value={network} key={network}>
                                                {network}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <div>
                                    {ethWalletAddress ? (
                                        <p>
                                            Wallet: {(ethWalletBalance / 1e18).toFixed(4)} {network === 'mainnet' ? 'ETH' : 'FTM'} -{' '}
                                            {formatWalletAddress(ethWalletAddress)}
                                        </p>
                                    ) : (
                                        <span>Invalid private key!</span>
                                    )}
                                </div>
                                {ethWalletAddress && (
                                    <button className='copy-btn' onClick={() => ethWalletAddress && copyToClipboard(ethWalletAddress)}></button>
                                )}
                            </div>
                            <div className='eth-deploy'>
                                <button
                                    onClick={async () => {
                                        const [address, contractBody] = await deployETHToken(
                                            ethLiquidity.toString(),
                                            description,
                                            name,
                                            symbol,
                                            ethPrivateKey,
                                            network,
                                            solidityReleases,
                                            '98',
                                        );
                                        navigate(`/ErcTokenCreate?walletAddress=${walletAddress}&sessionId=${sessionId}`, {
                                            state: {
                                                initLiquidity: ethLiquidity,
                                                description: description,
                                                name: name,
                                                symbol: symbol,
                                                address: address,
                                                contractBody: contractBody,
                                                snipePrivateKey: snipeEthChecked ? ethSniperPrivateKey : '',
                                                snipeValue: snipeEthChecked ? ethSnipeValue : '0',
                                            },
                                        });
                                    }}
                                >
                                    ERC Deploy
                                </button>
                                <div className='eth-deploy-inputs'>
                                    <label>
                                        <b>Liquidity</b>
                                        <input type='number' value={ethLiquidity} onChange={(event) => setEthLiquidity(Number(event.target.value))} />
                                    </label>
                                    <div className='second-wallet-snipe-container second-wallet-snipe-container--eth'>
                                        <label>
                                            <input
                                                type='checkbox'
                                                className='autocap-checkbox'
                                                checked={snipeEthChecked}
                                                onChange={() => setSnipeEthChecked(!snipeEthChecked)}
                                            />
                                            <b>Snipe w/ second wallet</b>
                                        </label>
                                        <label>
                                            <input
                                                className='second-wallet-snipe-input'
                                                value={ethSnipeValue}
                                                type='number'
                                                max={100}
                                                min={0}
                                                onChange={(e) => {
                                                    const value = parseFloat(e.target.value);

                                                    setEthSnipeValue(Math.max(0, Math.min(100, value)));
                                                }}
                                            />
                                            <b>%</b>
                                        </label>
                                    </div>
                                    <label>
                                        <input
                                            checked={ethAutoOpen}
                                            onChange={(e) => {
                                                setEthAutoOpen(e.target.checked);
                                            }}
                                            type='checkbox'
                                        />
                                        <b>Auto Open</b>
                                    </label>
                                </div>
                            </div>
                        </section>
                    </div>
                    <div className='tg-create-container'>
                        <h3>Telegram Channel Creation</h3>
                        <div>{!authorized && <TelegramLogin />}</div>
                        <div className='tg'>{authorized && <RegisterChannel preventSymbols={preventSymbols} />}</div>
                    </div>
                </section>
            </section>
        </>
    );
}
